import React, { useState, useRef, useEffect } from 'react';
import { Smile, Search } from 'lucide-react';
import { emojiMetadata } from './emojiMetadata';

const EmojiPicker = ({ onEmojiSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const pickerRef = useRef(null);
  const searchInputRef = useRef(null);
  
  // Add search input styles with adjusted padding for the icon
  const searchInputStyles = {
    width: '100%',
    height: '36px',
    paddingLeft: '12px',
    paddingRight: '36px',
    marginBottom: '12px',
    backgroundColor: 'rgba(55, 65, 81, 0.2)',
    border: '1px solid rgba(75, 85, 99, 0.3)',
    borderRadius: '6px',
    color: '#ffffff',
    fontSize: '14px',
    outline: 'none'
  };

  // Standard emoji categories with modern emoji set and reordered categories
  const emojiCategories = {
    smileys: ['😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🤩', '🥳', '😏', '😒', '😞', '😔', '😟', '😕', '😣', '😖', '😫', '😩', '😢', '😭', '😤', '😠', '😡', '🤬', '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰', '😥', '😓', '🤗', '🤔', '🤭', '🤫', '🤥', '😶', '😐', '😑', '😬', '🙄', '😯', '😦', '😧', '😮', '😲', '🥱', '😴', '🤤', '😪', '😵', '🤐', '🥴', '🤢', '🤮', '🤧', '😷', '🤒', '🤕'],

    art: ['🎨', '🖌️', '🖼️', '✏️', '📝', '✒️', '🖋️', '📏', '📐', '✂️', '🎭', '🎪', '🎬', '🎤', '🎧', '🎼', '🎹', '🖍️', '🖊️', '📚', '🗂️', '📋', '🎆', '🎇', '✨', '💫', '🌈', '🎠', '🎡', '🎢', '🎪', '🗿', '🏰', '💐', '🌺', '🌸', '🌼'],
    
    emotions: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '💌', '💋', '👥', '👀', '🧠'],
    
    people: ['👶', '👧', '🧒', '👦', '👩', '🧑', '👨', '👱‍♀️', '👱', '👴', '👵', '👲', '👳‍♀️', '👳', '👮‍♀️', '👮', '💂‍♀️', '💂', '👷‍♀️', '👷', '🤴', '👸', '🧙‍♀️', '🧙', '🧚‍♀️', '🧚', '🧛‍♀️', '🧛', '🧜‍♀️', '🧜', '🧝‍♀️', '🧝', '🧞‍♀️', '🧞', '💁', '🙅', '🙆', '🙋', '🧏', '🙇', '🤦', '🤷', '👨‍⚕️', '👨‍🎓', '👨‍🏫', '👨‍⚖️', '👨‍🌾', '👨‍🍳', '👨‍🔧', '👨‍🏭', '👨‍💼', '👨‍🔬', '👨‍💻', '👨‍🎤', '👨‍🎨', '👨‍✈️', '👨‍🚀', '👨‍🚒'],

    gestures: ['👋', '🤚', '🖐️', '✋', '🖖', '👌', '🤌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👍', '👎', '✊', '👊', '🤛', '🤜', '👏', '🙌', '👐', '🤲', '🤝', '🙏', '✍️', '💅', '🤳', '💪'],
    
    activities: ['🏃‍♀️', '🏃', '🚶‍♀️', '🚶', '💃', '🕺', '👯‍♀️', '👯', '🧖‍♀️', '🧖', '🧗‍♀️', '🧗', '🤺', '🏇', '⛷️', '🏂', '🏌️', '🏄‍♀️', '🏄', '🚣‍♀️', '🚣', '🏊‍♀️', '🏊', '⛹️‍♀️', '⛹️', '🏋️‍♀️', '🏋️', '🚴‍♀️', '🚴', '🚵‍♀️', '🚵', '🤸', '🤼', '🤽', '🤾', '🤹', '🎪', '🎭', '🎨', '🎬', '🎤', '🎧', '🎼', '🎹', '🥁', '🎷', '🎺', '🎸', '🎻', '🎲', '🎯', '🎳', '🎮'],

    nature: ['🌸', '💐', '🌷', '🌹', '🌺', '🌻', '🌼', '🌱', '🌲', '🌳', '🌴', '🌵', '🌾', '🌿', '☘️', '🍀', '🍁', '🍂', '🍃', '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🦁', '🐮', '🐷', '🐸', '🐵', '🐔', '🐧', '🐦', '🐤', '🦆', '🦅', '🦉', '🐺', '🐗', '🐴', '🦄', '🐝', '🐛', '🦋', '🐌', '🐞', '🐜', '🦂', '🐢', '🐍', '🦎', '🐙', '🦑', '🦐', '🦞', '🦀', '🐠', '🐟', '🐬', '🐳', '🐋', '🦈', '🐊', '🐅', '🐆', '🦓', '🦍', '🐘', '🦛', '🦏', '🐪', '🐫', '🦒', '🦘', '🐃', '🐂', '🐄', '🐎', '🐖', '🐏', '🐑', '🐐', '🦌', '🐕', '🐩', '🐈', '🐓', '🦃', '🦚', '🦜', '🦢', '🦩', '🕊️', '🐇', '🦝', '🦡', '🐁', '🐀', '🐿️', '🦔'],
    
    food: ['🍏', '🍎', '🍐', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🍈', '🍒', '🍑', '🍍', '🥥', '🥝', '🍅', '🍆', '🥑', '🥦', '🥬', '🥒', '🌶️', '🌽', '🥕', '🥔', '🍠', '🥐', '🥯', '🍞', '🥖', '🥨', '🧀', '🥚', '🍳', '🥞', '🥓', '🥩', '🍗', '🍖', '🌭', '🍔', '🍟', '🍕', '🥪', '🥙', '🌮', '🌯', '🥗', '🥘', '🥫', '🍝', '🍜', '🍲', '🍛', '🍣', '🍱', '🥟', '🍤', '🍙', '🍚', '🍘', '🍥', '🥠', '🍢', '🍡', '🍧', '🍨', '🍦', '🥧', '🧁', '🍰', '🎂', '🍮', '🍭', '🍬', '🍫', '🍿', '🍩', '🍪', '🌰', '🥜', '🍯', '🥛', '🍼', '☕', '🍵', '🥤', '🍶', '🍺', '🍻', '🥂', '🍷', '🥃', '🍸', '🍹', '🧉', '🍾', '🥄', '🍴', '🍽️', '🥢'],
    
    objects: ['⌚', '📱', '📲', '💻', '⌨️', '🖥️', '🖨️', '🖱️', '🕹️', '💽', '💾', '💿', '📀', '📼', '📷', '📸', '📹', '🎥', '📞', '☎️', '📟', '📠', '📺', '📻', '⏰', '⌛', '⏳', '📡', '🔋', '🔌', '💡', '🔦', '🕯️', '💸', '💵', '💴', '💶', '💷', '💰', '💳', '💎', '⚖️', '🔧', '🔨', '⚒️', '🛠️', '⛏️', '🔩', '⚙️', '⛓️', '🔫', '💣', '🔪', '🗡️', '⚔️', '🛡️', '🚬', '⚰️', '⚱️', '🏺', '🔮', '📿', '💈', '⚗️', '🔭', '🔬', '💊', '💉', '🩸', '🩹', '🩺', '🌡️', '🧬', '🧪', '🧫', '🧭', '🔍', '🔎', '🔑', '🗝️', '🔐', '🔓', '🔒']
  };

  const getFilteredCategories = () => {
  if (!searchTerm) return emojiCategories;
  
  const filtered = {};
  const searchTermLower = searchTerm.toLowerCase();
  
  Object.entries(emojiCategories).forEach(([category, emojis]) => {
    const filteredEmojis = emojis.filter(emoji => {
      // Check if emoji matches directly
      if (emoji.includes(searchTermLower)) return true;
      
      // Check if category matches
      if (category.toLowerCase().includes(searchTermLower)) return true;
      
      // Check if any keywords match
      const keywords = emojiMetadata[emoji] || [];
      return keywords.some(keyword => 
        keyword.toLowerCase().includes(searchTermLower)
      );
    });
    
    if (filteredEmojis.length > 0) {
      filtered[category] = filteredEmojis;
    }
  });
  
  return filtered;
};
  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when picker opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const pickerStyles = {
    container: {
      position: 'absolute',
      bottom: '100%',
      right: '40px',
      marginBottom: '8px',
      padding: '16px',
      borderRadius: '8px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      backgroundColor: 'rgba(31, 41, 55, 0.95)',
      backdropFilter: 'blur(8px)',
      border: '1px solid rgba(75, 85, 99, 0.3)',
      width: '320px',
      maxHeight: '400px',
      overflowY: 'auto',
      zIndex: 1000
    },
    categoryTitle: {
      color: 'white',
      fontSize: '14px',
      marginBottom: '8px',
      textTransform: 'capitalize'
    },
    emojiGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(8, 1fr)',
      gap: '4px'
    },
    emojiButton: {
      padding: '4px',
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '20px',
      width: '32px',
      height: '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
   searchIconWrapper: {
      position: 'absolute',
      right: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      pointerEvents: 'none',
      color: '#9ca3af',
      height: '16px'
    }
  };

  return (
    <div style={{ position: 'relative', width: '0', height: '0' }} ref={pickerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: 'absolute',
          right: '44px',
          top: '18px',
          transform: 'translateY(-50%)',
          padding: '6px',
          backgroundColor: 'transparent',
          border: 'none',
          color: '#9ca3af',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1
        }}
      >
        <Smile size={16} />
      </button>

      {isOpen && (
        <div style={pickerStyles.container}>
          <div style={{ position: 'relative', marginBottom: '12px' }}>
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search emojis..."
              style={searchInputStyles}
            />
            <div style={pickerStyles.searchIconWrapper}>
              <Search size={16} />
            </div>
          </div>
          
          {Object.entries(getFilteredCategories()).map(([category, emojis]) => (
            <div key={category} style={{ marginBottom: '16px' }}>
              <h3 style={pickerStyles.categoryTitle}>
                {category}
              </h3>
              <div style={pickerStyles.emojiGrid}>
                {emojis.map((emoji, index) => (
                  <button
                    key={`${category}-${index}`}
                    onClick={() => {
                      onEmojiSelect(emoji);
                      setIsOpen(false);
                      setSearchTerm('');
                    }}
                    style={pickerStyles.emojiButton}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EmojiPicker;